import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';

/**
 * Enhanced Pull-to-Refresh component with better mobile support
 * - Haptic feedback on supported devices
 * - Better visual feedback and animations
 * - Fixes Android Chrome native pull conflict
 * - Improved touch handling
 */
const PullToRefresh = ({ onRefresh, children }) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const threshold = 70; // Reduced threshold for better UX
  
  // Device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;

  // Haptic feedback (if supported)
  const triggerHaptic = useCallback((type = 'light') => {
    try {
      if ('vibrate' in navigator) {
        switch (type) {
          case 'light':
            navigator.vibrate(10);
            break;
          case 'medium':
            navigator.vibrate(20);
            break;
          case 'success':
            navigator.vibrate([10, 50, 10]);
            break;
          default:
            navigator.vibrate(10);
        }
      }
    } catch (error) {
      // Silently fail if haptic feedback not supported
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let touchStartY = 0;
    let isAtTop = true;
    let lastPullDistance = 0;

    const handleTouchStart = (e) => {
      // Only activate if at top of page
      if (window.scrollY === 0 && document.documentElement.scrollTop === 0) {
        touchStartY = e.touches[0].clientY;
        startY.current = touchStartY;
        isAtTop = true;
        lastPullDistance = 0;
        
        // Prevent default browser pull-to-refresh
        if (isStandalone) {
          document.body.style.overscrollBehaviorY = 'none';
          document.documentElement.style.overscrollBehaviorY = 'none';
        }
      } else {
        isAtTop = false;
      }
    };

    const handleTouchMove = (e) => {
      if (!isAtTop || isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const deltaY = currentY - touchStartY;

      // Only handle downward pull at top of page
      if (deltaY > 0 && window.scrollY === 0) {
        // Prevent default browser behavior
        e.preventDefault();
        
        // Calculate pull distance with resistance
        const resistance = isIOS ? 0.45 : 0.4; // iOS has slightly less resistance
        const maxDistance = 100;
        const distance = Math.min(deltaY * resistance, maxDistance);
        
        setPullDistance(distance);
        
        // Trigger haptic feedback at threshold
        if (distance >= threshold && lastPullDistance < threshold) {
          triggerHaptic('medium');
        }
        
        setIsPulling(distance > 10);
        lastPullDistance = distance;
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance > threshold && !isRefreshing) {
        setIsRefreshing(true);
        triggerHaptic('success');
        
        try {
          await onRefresh();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          // Small delay for better UX
          setTimeout(() => {
            setIsRefreshing(false);
          }, 300);
        }
      }
      
      setIsPulling(false);
      setPullDistance(0);
      
      // Reset overscroll behavior
      if (isStandalone) {
        document.body.style.overscrollBehaviorY = '';
        document.documentElement.style.overscrollBehaviorY = '';
      }
    };

    const handleTouchCancel = () => {
      setIsPulling(false);
      setPullDistance(0);
      if (isStandalone) {
        document.body.style.overscrollBehaviorY = '';
        document.documentElement.style.overscrollBehaviorY = '';
      }
    };

    // Passive: false is required to preventDefault
    const options = { passive: false };
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, options);
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      
      // Cleanup
      document.body.style.overscrollBehaviorY = '';
      document.documentElement.style.overscrollBehaviorY = '';
    };
  }, [onRefresh, pullDistance, isRefreshing, threshold, isStandalone, isIOS, triggerHaptic]);

  // Calculate visual feedback properties
  const progress = Math.min((pullDistance / threshold) * 100, 100);
  const indicatorScale = Math.min(0.5 + (pullDistance / threshold) * 0.5, 1);
  const shouldShowIndicator = isPulling || isRefreshing;

  return (
    <Box 
      ref={containerRef} 
      className="pull-to-refresh-container"
      sx={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
      }}
    >
      {/* Pull-to-refresh indicator */}
      <Box
        className="pull-to-refresh-indicator"
        sx={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 0px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: 'background.paper',
          boxShadow: shouldShowIndicator ? 2 : 0,
          opacity: shouldShowIndicator ? 1 : 0,
          transition: shouldShowIndicator ? 'box-shadow 0.2s ease, opacity 0.2s ease' : 'opacity 0.3s ease',
          pointerEvents: 'none',
          willChange: 'transform, opacity',
          // Smooth animation
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        style={{
          // Use style for dynamic values that change frequently
          transform: `translateX(-50%) translateY(${Math.min(pullDistance - 50, 20)}px) scale(${indicatorScale})`,
        }}
      >
        {isRefreshing ? (
          <CircularProgress 
            size={24} 
            thickness={4}
            sx={{ color: 'primary.main' }}
          />
        ) : (
          <RefreshRounded 
            sx={{ 
              fontSize: 24,
              color: progress >= 100 ? 'primary.main' : 'text.secondary',
              transform: `rotate(${Math.min(pullDistance * 3, 360)}deg)`,
              transition: 'color 0.2s ease',
            }} 
          />
        )}
      </Box>
      
      {/* Content */}
      {children}
    </Box>
  );
};

export default PullToRefresh;
